/** Season detail — championship events with category filter */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ArrowLeft, MapPin, Calendar } from 'lucide-react';
import type { GetChampionshipResponse } from '@/types/telemetry';

interface SeasonDetailProps {
  championshipId: string;
}

export function SeasonDetail({ championshipId }: SeasonDetailProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [data, setData] = useState<GetChampionshipResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);


  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetChampionshipResponse>('GetChampionship', { championship_id: championshipId });
      setData(res);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested championship could not be found.' : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [championshipId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter events (for now no category filtering on events since events are shared)
  const filteredEvents = useMemo(() => {
    if (!data) return [];
    return [...data.events].sort((a, b) => (b.dateStart || '').localeCompare(a.dateStart || ''));
  }, [data]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!data) return <ErrorState message={t('races.noData')} />;

  const { championship } = data;

  return (
    <div>
      {/* Back + Header */}
      <button
        onClick={() => navigate('#/races')}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">{championship.name}</h1>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-2xl font-bold gradient-text">{championship.year}</span>
          <span className="text-xs text-text-muted">· {championship.country}</span>
        </div>
      </div>



      {/* Event list */}
      <h2 className="section-title mb-4">{t('races.events', { n: filteredEvents.length })}</h2>

      {filteredEvents.length === 0 ? (
        <p className="text-sm text-text-muted py-8 text-center">{t('races.noEvents')}</p>
      ) : (
        <div className="space-y-3">
          {filteredEvents.map(event => (
            <button
              key={event.id}
              onClick={() => navigate(`#/races/${championshipId}/${event.id}`)}
              className="card-interactive w-full text-left p-4 hover:border-border-hover hover:bg-bg-hover group"
            >
              <div className="flex items-center gap-4">
                {/* Round number */}
                <div className="w-10 h-10 rounded-lg bg-bg-surface border border-border-subtle flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold mono text-accent">R{event.roundNumber}</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-accent transition-colors">
                    {event.trackName}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                    <span className="flex items-center gap-1">
                      <MapPin size={11} />
                      {event.country}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(event.dateStart)}
                    </span>
                  </div>
                </div>

                {/* Arrow */}
                <ArrowLeft size={14} className="text-text-muted rotate-180 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '–';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
